import React from 'react';
import { 
  FaBullhorn, FaPlus, FaTimes, FaCalendarAlt, FaTrash, 
  FaVideo, FaChalkboardTeacher, FaRocket, FaSpinner, FaCheck
} from 'react-icons/fa';

const AnnouncementsSection = ({ 
  announcements,
  showModal,
  setShowModal,
  announcementForm,
  setAnnouncementForm,
  handleCreate,
  handleDelete,
  submitting
}) => {
  const typeIcons = {
    training: FaChalkboardTeacher,
    webinar: FaVideo,
    seminar: FaChalkboardTeacher,
    service_launch: FaRocket,
    general: FaBullhorn
  };

  return (
    <>
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
              <FaBullhorn className="text-[#FFD700]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Announcements</h2>
              <p className="text-gray-400 text-sm">Share updates with your clients</p>
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center space-x-2"
            data-testid="create-announcement-btn"
          >
            <FaPlus size={14} /><span>New Announcement</span>
          </button>
        </div>

        {announcements.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <FaBullhorn className="text-4xl mx-auto mb-2 opacity-50" />
            <p>No announcements yet. Create one to share updates with your clients.</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {announcements.map((ann) => {
              const Icon = typeIcons[ann.announcement_type] || FaBullhorn;
              return (
                <div key={ann.id} className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F] flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-[#FFD700]/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="text-[#FFD700]" size={14} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-white font-bold text-sm">{ann.title}</span>
                        {ann.priority === 'high' && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">High Priority</span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-2">{ann.description}</p>
                      {ann.event_date && (
                        <p className="text-gray-500 text-xs mt-1 flex items-center space-x-1">
                          <FaCalendarAlt size={10} />
                          <span>{new Date(ann.event_date).toLocaleDateString('en-IN')}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(ann.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Announcement Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A2942] rounded-xl border border-[#2D5A8F] max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#2D5A8F] flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                  <FaBullhorn className="text-[#FFD700]" />
                </div>
                <h3 className="text-xl font-bold text-white">Create Announcement</h3>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-[#0A1929] rounded-lg">
                <FaTimes className="text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Type</label>
                <select
                  value={announcementForm.announcement_type}
                  onChange={(e) => setAnnouncementForm(prev => ({ ...prev, announcement_type: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                >
                  <option value="general">General Update</option>
                  <option value="training">Live Training</option>
                  <option value="webinar">Webinar</option>
                  <option value="seminar">Seminar</option>
                  <option value="service_launch">New Service Launch</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Title *</label>
                <input
                  type="text"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Join our Live Webinar on Tax Planning"
                  className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Description *</label>
                <textarea
                  value={announcementForm.description}
                  onChange={(e) => setAnnouncementForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your announcement..."
                  rows={3}
                  className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Event Date (Optional)</label>
                  <input
                    type="date"
                    value={announcementForm.event_date}
                    onChange={(e) => setAnnouncementForm(prev => ({ ...prev, event_date: e.target.value }))}
                    className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Event Time (Optional)</label>
                  <input
                    type="time"
                    value={announcementForm.event_time}
                    onChange={(e) => setAnnouncementForm(prev => ({ ...prev, event_time: e.target.value }))}
                    className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Registration/Join Link (Optional)</label>
                <input
                  type="url"
                  value={announcementForm.event_link}
                  onChange={(e) => setAnnouncementForm(prev => ({ ...prev, event_link: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Priority</label>
                <div className="flex space-x-4">
                  {['low', 'normal', 'high'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setAnnouncementForm(prev => ({ ...prev, priority: p }))}
                      className={`flex-1 py-2 rounded-lg font-medium capitalize transition-all ${
                        announcementForm.priority === p
                          ? p === 'high'
                            ? 'bg-red-500/20 text-red-400 border border-red-500'
                            : p === 'normal'
                            ? 'bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500'
                          : 'bg-[#0F172A] text-gray-400 border border-[#2D5A8F] hover:border-gray-500'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-[#2D5A8F] flex space-x-4">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={submitting || !announcementForm.title || !announcementForm.description}
                className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="publish-announcement-btn"
              >
                {submitting ? <FaSpinner className="animate-spin" /> : <FaBullhorn />}
                <span>{submitting ? 'Publishing...' : 'Publish Announcement'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnnouncementsSection;
